/**
 * Unified Tab System - Icon Registry
 * @module lib/tabs/icons
 */

import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Award,
  BarChart3,
  Bell,
  Briefcase,
  Building2,
  Calendar,
  CalendarDays,
  CalendarRange,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clipboard,
  ClipboardCheck,
  Clock,
  Clock4,
  Copy,
  Download,
  Edit,
  ExternalLink,
  Eye,
  File,
  FileArchive,
  FileText,
  Filter,
  Folder,
  FolderOpen,
  HelpCircle,
  Home,
  Info,
  Link as LinkIcon,
  Lock,
  Mail,
  MessageSquare,
  MoreHorizontal,
  PieChart,
  Plus,
  Receipt,
  Search,
  Settings,
  Shield,
  Trash2,
  TrendingUp,
  Upload,
  User,
  UserCheck,
  UserCircle,
  UserPlus,
  Users,
  X,
  XCircle,
} from "lucide-react";

/**
 * Icon registry for tab navigation
 * Provides type-safe icon lookup for static tab configs
 */
export const TAB_ICONS = {
  Home,
  User,
  UserCircle,
  Calendar,
  CalendarDays,
  CalendarRange,
  Clock,
  Clock4,
  FileText,
  FileArchive,
  File,
  FolderOpen,
  Folder,
  Users,
  UserPlus,
  UserCheck,
  Award,
  Briefcase,
  Building2,
  Activity,
  Settings,
  HelpCircle,
  Shield,
  Lock,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  BarChart3,
  PieChart,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  Bell,
  Mail,
  MessageSquare,
  Check,
  CheckCircle2,
  X,
  XCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  ExternalLink,
  Link: LinkIcon,
  Copy,
  Clipboard,
  ClipboardCheck,
  Receipt,
} as const;

/**
 * Icon name type (keys of TAB_ICONS)
 */
export type TabIconName = keyof typeof TAB_ICONS;

/**
 * Get icon component by name with optional fallback
 */
export function getTabIcon(name: TabIconName, fallback?: LucideIcon): LucideIcon {
  const icon = TAB_ICONS[name];
  if (!icon && fallback) {
    return fallback;
  }
  if (!icon) {
    console.warn(`[tabs] Icon "${name}" not found in registry`);
    return FileText; // Safe fallback
  }
  return icon;
}

/**
 * Check if a name is a valid icon name
 */
export function isValidIconName(name: string): name is TabIconName {
  return name in TAB_ICONS;
}
